import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const StakeholderCommunications = ({ messages, onSendMessage }) => {
  const [newMessage, setNewMessage] = useState('');
  const [selectedThread, setSelectedThread] = useState('all');

  const threads = [
    { id: 'all', label: 'All Messages', count: messages?.length },
    { id: 'legal', label: 'Legal Team', count: messages?.filter(m => m?.thread === 'legal')?.length },
    { id: 'technical', label: 'Technical Team', count: messages?.filter(m => m?.thread === 'technical')?.length },
    { id: 'financial', label: 'Financial Team', count: messages?.filter(m => m?.thread === 'financial')?.length }
  ];

  const filteredMessages = selectedThread === 'all' 
    ? messages 
    : messages?.filter(m => m?.thread === selectedThread);

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (newMessage?.trim()) {
      onSendMessage({
        content: newMessage,
        thread: selectedThread === 'all' ? 'general' : selectedThread
      });
      setNewMessage('');
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return messageTime?.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Stakeholder Communications</h3>
        <Button variant="outline" iconName="Users" iconPosition="left">
          Manage Participants
        </Button>
      </div>
      {/* Thread Filter */}
      <div className="flex space-x-2 overflow-x-auto">
        {threads?.map((thread) => (
          <button
            key={thread?.id}
            onClick={() => setSelectedThread(thread?.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-smooth ${
              selectedThread === thread?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <span>{thread?.label}</span>
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${
              selectedThread === thread?.id
                ? 'bg-primary-foreground/20 text-primary-foreground'
                : 'bg-background text-muted-foreground'
            }`}>
              {thread?.count}
            </span>
          </button>
        ))}
      </div>
      {/* Messages Container */}
      <div className="bg-card border border-border rounded-lg">
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {filteredMessages?.map((message) => (
            <div key={message?.id} className={`flex ${message?.isOwn ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex max-w-xs lg:max-w-md ${message?.isOwn ? 'flex-row-reverse' : 'flex-row'} space-x-2`}>
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-muted">
                    <Image 
                      src={message?.avatar} 
                      alt={message?.sender}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                <div className={`${message?.isOwn ? 'mr-2' : 'ml-2'}`}>
                  <div className={`p-3 rounded-lg ${
                    message?.isOwn 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted text-foreground'
                  }`}>
                    {!message?.isOwn && (
                      <p className="text-xs font-medium mb-1 opacity-80">{message?.sender}</p>
                    )}
                    <p className="text-sm">{message?.content}</p>
                    
                    {message?.attachment && (
                      <div className="mt-2 p-2 bg-background/10 rounded border">
                        <div className="flex items-center space-x-2">
                          <Icon name={message?.attachment?.type === 'document' ? 'FileText' : 'Image'} size={16} />
                          <span className="text-xs">{message?.attachment?.name}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6">
                            <Icon name="Download" size={12} />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className={`flex items-center mt-1 space-x-2 ${message?.isOwn ? 'justify-end' : 'justify-start'}`}>
                    <span className="text-xs text-muted-foreground">{formatTime(message?.timestamp)}</span>
                    {message?.isOwn && (
                      <Icon 
                        name={message?.read ? 'CheckCheck' : 'Check'} 
                        size={12} 
                        className={message?.read ? 'text-accent' : 'text-muted-foreground'} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Message Input */}
        <div className="border-t border-border p-4">
          <form onSubmit={handleSendMessage} className="flex items-end space-x-3">
            <div className="flex-1">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e?.target?.value)}
                placeholder="Type your message..."
                rows={2}
                className="w-full p-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' && !e?.shiftKey) {
                    e?.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="icon" type="button">
                <Icon name="Paperclip" size={16} />
              </Button>
              <Button 
                type="submit" 
                disabled={!newMessage?.trim()}
                iconName="Send"
                iconPosition="left"
              >
                Send
              </Button>
            </div>
          </form>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-accent" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">Schedule Meeting</h4>
              <p className="text-sm text-muted-foreground">Coordinate with stakeholders</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">Urgent Request</h4>
              <p className="text-sm text-muted-foreground">Flag important messages</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-success" />
            </div>
            <div>
              <h4 className="font-medium text-foreground">Share Document</h4>
              <p className="text-sm text-muted-foreground">Attach files to discussion</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StakeholderCommunications;